import {
    Button,
    createStyles,
    FormControlLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Tooltip,
    WithStyles,
    withStyles,
} from '@material-ui/core';
import { AttachFile } from '@material-ui/icons';
import _ from 'lodash';
import { reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { DefaultObservation, IObservation, IObservationType, IUser } from 'src/Definitions';
import { IStores } from 'src/Stores';
import ObservationTypes from 'src/utils/ObservationTypes';
import Carousel from './Carousel';
import ObservationEditorLogic from './ObservationEditorLogic';
import ObservationsLogic from './ObservationsLogic';

const styles = createStyles({
    gridContainer: {
        paddingBottom: '20px',
        paddingLeft: '20px',
        paddingTop: '60px',
    },
    pageContainer: {
        padding: 20,
    },
});

interface IObservationEditorProps extends WithStyles<typeof styles> {
    assessmentId?: string;
    behaviorId?: string;
    getAllObservationTypes?: (pageNumber?: number, pageSize?: number) => Promise<void>;
    isCarouselOpen?: boolean;
    isLoading?: boolean;
    observation?: IObservation;
    observationTypes?: IObservationType[];
    user?: IUser;
    saveObservation?: (observation: IObservation) => Promise<void>;
    resetSelectedObservation?: () => void;
    toggleCarousel?: () => void;
    updateObservationTable?: (behaviorId: string) => void;
}

@observer
class ObservationEditor extends React.Component<IObservationEditorProps> {
    private _observationEditorLogic: ObservationEditorLogic = new ObservationEditorLogic();
    private _observationLogic: ObservationsLogic = new ObservationsLogic();

    public state = {
        observationDescription: this._observationEditorLogic.getObservationDescriptionOrDefault(
            this.props.observation,
            this.props.behaviorId
        ),
        observationType: this._observationEditorLogic.getObservationTypeNameOrDefault(
            this.props.observation,
            this.props.behaviorId
        ),
    };

    public componentDidMount() {
        this.props.getAllObservationTypes!(undefined, 5000);
    }

    public resetObservationType = reaction(
        () => this.props.observation,
        (observation) => {
            this.setState({
                observationDescription: this._observationEditorLogic.getObservationDescriptionOrDefault(
                    observation,
                    this.props.behaviorId
                ),
                observationType: this._observationEditorLogic.getObservationTypeNameOrDefault(
                    observation,
                    this.props.behaviorId
                ),
            });
        }
    );

    public addObservation = async (event: React.MouseEvent) => {
        if (this.props.observation && this.props.behaviorId !== this.props.observation.behaviorId) {
            return;
        } else {
            const observationToSave = {
                ...DefaultObservation(true),
                ...this.props.observation,
                assessmentId: this.props.assessmentId,
                behaviorId: this.props.behaviorId,
                observationDescription: this.state.observationDescription,
                observationType: this._observationEditorLogic.getObservationType(
                    this.props.observationTypes,
                    this.state.observationType || ''
                ),
                observer: this._observationEditorLogic.getObserver(
                    this.props.user,
                    this.props.observation
                ),
            } as IObservation;
            if (observationToSave.isNew && observationToSave.observationDescription === '') {
                return;
            } else {
                await this.props.saveObservation!(observationToSave);
                event.preventDefault();
                this.clearObservation();
                this.props.updateObservationTable!(this.props.behaviorId!);
            }
        }
    };

    public handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ observationType: event.target.value });
        if (this.props.observation) {
            this.props.observation.isModified = true;
        }
    };

    public handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ observationDescription: event.target.value });
        if (this.props.observation) {
            this.props.observation.isModified = true;
        }
    };

    public clearObservation = () => {
        this.props.resetSelectedObservation!();
        this.setState({ observationDescription: '', observationType: ObservationTypes.Rose });
    };

    public clearObservationIfDifferentBehavior = () => {
        if (this.props.observation && this.props.observation.behaviorId !== this.props.behaviorId) {
            this.clearObservation();
        }
    };

    public getSaveButtonColor = () => {
        if (
            this.props.observation === undefined ||
            (this.props.observation.isModified &&
                (this.props.observation.id === undefined ||
                    (this.props.behaviorId &&
                        this.props.observation!.behaviorId === this.props.behaviorId)))
        ) {
            return 'primary';
        }
        return 'default';
    };

    public renderRadioButton(type: string) {
        const color = this._observationLogic.getColorFromObservationType(type);
        return (
            <FormControlLabel
                value={type}
                control={
                    <Radio
                        style={{
                            color,
                        }}
                    />
                }
                label={type}
            />
        );
    }

    public openCarousel = () => {
        this.clearObservationIfDifferentBehavior();
        this.props.toggleCarousel!();
    };

    public render() {
        const { behaviorId, observation, classes, isLoading } = this.props;
        const carousel = this.props.isCarouselOpen && <Carousel />;
        return (
            <>
                <Grid className={classes.pageContainer} container direction="column">
                    <Grid
                        alignItems="center"
                        className={classes.gridContainer}
                        container
                        justify="space-between"
                        wrap="nowrap"
                    >
                        <Grid container alignItems="center">
                            <Grid item xs={12}>
                                <TextField
                                    label="Input Observation"
                                    multiline={true}
                                    fullWidth
                                    rows={10}
                                    variant="outlined"
                                    value={this.state.observationDescription}
                                    onChange={this.handleDescriptionChange}
                                    onClick={this.clearObservationIfDifferentBehavior}
                                />
                            </Grid>
                            <Grid item xs={11}>
                                <RadioGroup
                                    row
                                    name="observationType"
                                    value={this.state.observationType}
                                    onChange={this.handleRadioChange}
                                >
                                    {this.renderRadioButton(ObservationTypes.Rose)}
                                    {this.renderRadioButton(ObservationTypes.Thorn)}
                                    {this.renderRadioButton(ObservationTypes.Bud)}
                                </RadioGroup>
                            </Grid>
                            <Grid item xs={1}>
                                <Tooltip
                                    title={
                                        this._observationEditorLogic.shouldDisableAttachmentButton(
                                            behaviorId,
                                            observation
                                        ) ||
                                        this._observationEditorLogic.hasObservationChanged(
                                            this.props.observation
                                        )
                                            ? 'Please save observation before adding attachments'
                                            : ''
                                    }
                                >
                                    <div>
                                        <IconButton
                                            aria-label="Attach Pictures"
                                            color="primary"
                                            onClick={this.openCarousel}
                                            disabled={this._observationEditorLogic.shouldDisableAttachmentButton(
                                                behaviorId,
                                                observation
                                            )}
                                        >
                                            <AttachFile />
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    disabled={isLoading}
                                    onClick={this.clearObservation}
                                    variant="contained"
                                >
                                    Cancel
                                </Button>
                                &nbsp;&nbsp;
                                <Button
                                    disabled={isLoading}
                                    onClick={this.addObservation}
                                    variant="contained"
                                    color={this.getSaveButtonColor()}
                                >
                                    Save
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {carousel}
            </>
        );
    }
}

const InjectedObservationEditor = inject<
    IStores,
    IObservationEditorProps,
    Partial<IObservationEditorProps>,
    any
>((stores: IStores) => ({
    getAllObservationTypes: stores.observationTypes.getAllObservationTypes,
    isCarouselOpen: stores.observations.isCarouselOpen,
    isLoading: stores.observations.isLoading,
    observation: stores.observations.selectedObservation,
    observationTypes: stores.observationTypes.observationTypes,
    resetSelectedObservation: stores.observations.resetSelectedObservation,
    saveObservation: stores.observations.saveObservation,
    toggleCarousel: stores.observations.toggleCarousel,
    user: stores.global.user,
}))(withStyles(styles)(ObservationEditor));

export default InjectedObservationEditor;



// WEBPACK FOOTER //
// ./src/chevrons/behaviors/observations/ObservationEditor.tsx